/**
 * Tank Physics — gravity, penetration-based terrain resolution, knockback sliding, and ragdoll bouncing.
 *
 * Three states:
 * - Settled: at rest, skip physics until disturbed
 * - Walking: walkDeltaX != 0 || walkCatchupTimer > 0 — probe-and-snap (collision + downward scan)
 * - Physics-driven: everything else — gravity → velocity → penetration resolve
 */

import { PHYSICS, WORLD, TUMBLE, SLOPE, GRAPPLE, TRACTION, CLIMB_FX, WALL_BOUNCE, CEILING_BOUNCE, LAUNCH_TRAIL, BURST_TRAIL, BURST, HOP, FAST_FALL, PLATFORM, CELL, TERRAIN, SIDE_DEATH } from '../constants';
import type { Player } from '../types';
import { getSurfaceNormal, isPointInTerrain, resolveTerrainPenetration, getCellAt, getGrid } from '../terrain/grid';
import type { ParticleSystem } from '../rendering/particles';
import type { ScreenShake } from '../rendering/screen-shake';
import type { BumperRenderer } from '../rendering/bumpers';
import { mirrorExitTumble, mirrorAirborne, mirrorSettle, mirrorWalking, mirrorEnterRecovery, assertActionStateConsistent } from './transitions';
import { lookupTank } from '../tanks/registry';

/** Clamp X to world boundaries accounting for tank radius */
export function clampToWorldX(x: number, ecbRadius: number = PHYSICS.ECB_RADIUS): number {
  return Math.max(WORLD.WORLD_LEFT + ecbRadius, Math.min(x, WORLD.WORLD_RIGHT - ecbRadius));
}

/** Result from wall bounce — used by caller to trigger VFX */
export interface WallBounceResult {
  side: 'left' | 'right';
  isSpike: boolean;
  intensity: number; // 0..1 normalized impact strength
}

/** Damage-scaled pinball bounce off world edges. Only triggers for ragdoll tanks.
 *  Returns null when SIDE_DEATH.OPEN_SIDES is true (tumble tanks fly off edges instead). */
function handleWorldWallBounce(p: Player, ecb: number): WallBounceResult | null {
  if (SIDE_DEATH.OPEN_SIDES) return null;

  // Only ragdoll tanks bounce — walking/aiming tanks hard-clamp
  if (!p.tumble) return null;

  const minX = WORLD.WORLD_LEFT + ecb;
  const maxX = WORLD.WORLD_RIGHT - ecb;

  let side: 'left' | 'right';
  if (p.x <= minX) {
    side = 'left';
    p.x = minX;
  } else if (p.x >= maxX) {
    side = 'right';
    p.x = maxX;
  } else {
    return null; // not touching a wall
  }

  // Skip if velocity is already moving away from the wall (already bounced)
  if (side === 'left' && p.vx > 0) return null;
  if (side === 'right' && p.vx < 0) return null;

  // Damage-scaled bounce multiplier: stronger bounce at higher damage %
  const damageMult = Math.min(
    1 + p.damagePercent / WALL_BOUNCE.DAMAGE_SCALE,
    WALL_BOUNCE.MAX_BOUNCE_MULT,
  );

  // Reflect horizontal velocity
  p.vx = -p.vx * WALL_BOUNCE.BASE_RETENTION * damageMult;

  // Spike detection: steep downward trajectory into wall = spike (no upward pop)
  const speed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
  const spikeRatio = speed > 0.1 ? Math.abs(p.vy) / speed : 0;
  const isSpike = spikeRatio > WALL_BOUNCE.SPIKE_ANGLE_THRESHOLD && p.vy > 0;

  if (isSpike) {
    // Spike — preserve downward momentum (satisfying kills)
    p.vy *= WALL_BOUNCE.SPIKE_VY_RETENTION;
  } else {
    // Normal bounce — upward pop + dampen vertical
    p.vy *= WALL_BOUNCE.NON_SPIKE_VY_DAMPEN;
    p.vy += WALL_BOUNCE.POP_VY;
  }

  // Intensity for VFX scaling (0..1 based on pre-bounce speed)
  const intensity = Math.min(1.0, speed / WALL_BOUNCE.INTENSITY_DIVISOR);

  return { side, isSpike, intensity };
}

/** Result from ceiling bounce — used by caller to trigger VFX */
export interface CeilingBounceResult {
  intensity: number; // 0..1 normalized impact strength
}

/** Damage-scaled bounce off ceiling. Only triggers for ragdoll tanks.
 *  Returns bounce info for VFX, or null if no bounce occurred. */
function handleCeilingBounce(p: Player, ceilingY: number, ecb: number): CeilingBounceResult | null {
  if (!p.tumble) return null;                              // ragdoll only
  if (p.y - ecb > ceilingY) return null;   // not touching ceiling
  if (p.vy > 0) return null;                               // already moving down

  p.y = ceilingY + ecb;                    // clamp position

  const damageMult = Math.min(
    1 + p.damagePercent / CEILING_BOUNCE.DAMAGE_SCALE,
    CEILING_BOUNCE.MAX_BOUNCE_MULT,
  );
  const speed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);

  p.vy = -p.vy * CEILING_BOUNCE.BASE_RETENTION * damageMult;  // reflect vy
  // vx untouched — horizontal drift preserved

  return { intensity: Math.min(1.0, speed / CEILING_BOUNCE.INTENSITY_DIVISOR) };
}

/** Clear settled latch for all tanks near a crater so they re-snap to changed terrain. */
export function unsettleTanksNearCrater(players: Player[], cx: number, radius: number): void {
  const range = radius + PHYSICS.ECB_RADIUS * 2; // generous range
  for (const p of players) {
    if (!p.alive || !p.settled) continue;
    if (Math.abs(p.x - cx) < range) {
      p.settled = false;
      mirrorAirborne(p);
    }
  }
}

/** 4-way friction: ragdoll ground/air vs normal ground/air.
 *  During ragdoll immunity, always use airborne friction — prevents slopes from
 *  stealing velocity before the tank can escape the crater.
 *  Grounded ragdoll on slopes also uses airborne friction — tangent re-projection
 *  keeps the tank on the surface, grounded friction double-dips. */
function getFrameFriction(p: Player, isGrounded: boolean, ecb: number): number {
  if (p.tumble) {
    if (p.launchArmor > 0) return TUMBLE.AIRBORNE_FRICTION;
    if (!isGrounded) return TUMBLE.AIRBORNE_FRICTION;
    // Grounded ragdoll: check surface angle to distinguish slope from flat.
    const { ny } = getSurfaceNormal(p.x, p.y + ecb);
    const absNy = Math.abs(ny);
    if (absNy > SLOPE.WALL_NY && absNy < SLOPE.FLAT_NY) return TUMBLE.AIRBORNE_FRICTION;
    return TUMBLE.GROUNDED_FRICTION;
  }
  return isGrounded ? PHYSICS.GROUNDED_FRICTION : PHYSICS.AIRBORNE_FRICTION;
}

/** Reflect velocity off a surface normal with bounce retention + minimum upward bounce. */
function reflectBounce(p: Player, nx: number, ny: number): void {
  const dot = p.vx * nx + p.vy * ny;
  p.vx = (p.vx - 2 * dot * nx) * TUMBLE.UNTECH_BOUNCE_RETENTION;
  p.vy = (p.vy - 2 * dot * ny) * TUMBLE.UNTECH_BOUNCE_RETENTION;
  if (p.vy > -TUMBLE.MIN_BOUNCE_VY) p.vy = -TUMBLE.MIN_BOUNCE_VY;
}

/** Damped harmonic oscillator (rest = 1.0). Tuned for 60fps fixed timestep.
 *  DI/burst windows directly set p.squash for anticipation compression —
 *  this is safe because physics (and this spring) is frozen during those windows. */
function updateSquashSpring(p: Player): void {
  p.squashVel += (1.0 - p.squash) * CLIMB_FX.SPRING_STIFFNESS;
  p.squashVel *= CLIMB_FX.SPRING_DAMPING;
  p.squash += p.squashVel;
  p.squash = Math.max(CLIMB_FX.SQUASH_MIN, Math.min(CLIMB_FX.SQUASH_MAX, p.squash));
}

export interface TumbleConfig {
  hitstunPerSpeed: number;
  minHitstun: number;
  maxHitstun: number;
  fixedHitstun?: number;   // if set, skip speed scaling (recovery uses this)
  speedGated?: boolean;    // if true, only enter tumble if speed > TUMBLE.ENTRY_SPEED
  damagePercent?: number;  // if set, scale hitstun by damage% (low% = shorter lockout)
}

/** Enter tumble with hitstun + launch armor. Speed-gated and max-preserving. */
export function enterTumble(p: Player, config: TumbleConfig): void {
  const speed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
  if (config.speedGated && speed <= TUMBLE.ENTRY_SPEED) return;

  p.tumble = true;
  let total = config.fixedHitstun
    ?? Math.round(Math.min(config.maxHitstun, Math.max(config.minHitstun, speed * config.hitstunPerSpeed)));
  // Damage% scaling: low% = shorter lockout (60% at 0%, 100% at 120%+)
  if (config.damagePercent !== undefined && config.fixedHitstun === undefined) {
    const dmgScale = 0.6 + 0.4 * Math.min(config.damagePercent / 120, 1);
    total = Math.round(total * dmgScale);
  }
  // Split total into hitstun + recovery
  const hitstun = Math.round(total * TUMBLE.HITSTUN_FRACTION);
  const recovery = total - hitstun;
  // Max-preserve: only update if new timer is longer
  if (hitstun > p.hitstunFrames) {
    p.hitstunFrames = hitstun;
    p.tumbleRecoveryMax = recovery;
    p.tumbleRecoveryFrame = 0;
  }
  p.launchArmor = TUMBLE.LAUNCH_ARMOR_FRAMES;
  p.settled = false;
  p.walkCatchupTimer = 0; // kill stale walking state — prevents STATE A snap during tumble
}

function exitTumble(p: Player): void {
  if (!p.tumble) return;
  p.tumble = false;
  p.hitstunFrames = 0;
  p.launchArmor = 0;
  p.tumbleRecoveryFrame = 0;
  p.tumbleRecoveryMax = 0;
  p.rotation = 0; // snap upright — chaotic spin → clean upright = "I have control again"
  p.grappleLaunched = false;
  // NOTE: burstFlying intentionally NOT cleared here — gold trail persists
  // through ragdoll and after ragdoll exit. Cleared on landing.
  p.settled = false;
  p.walkCatchupTimer = 0; // kill stale walking state
  mirrorExitTumble(p);
}

/** Build a terrain test function that treats wireframe as pass-through based on player state.
 *  SAND = always solid. WIREFRAME = solid only when: not dropping, not tumbling, falling, probe below center. */
function makePlatformTest(p: Player): (wx: number, wy: number) => boolean {
  return (wx: number, wy: number): boolean => {
    const cell = getCellAt(wx, wy);
    if (cell === CELL.EMPTY) return false;
    if (cell === CELL.SAND) return true;
    // WIREFRAME — one-way platform logic
    if (p.platformDropTimer > 0) return false;  // dropping through
    if (p.tumble) return false;                  // ragdoll passes through
    if (p.vy < 0) return false;                  // moving upward
    if (wy <= p.y) return false;                 // probe above center = side/top
    return true;
  };
}

export function updateTankPhysics(
  players: Player[],
  particles?: ParticleSystem,
  screenShake?: ScreenShake,
  grappleRetractingIndex?: number,
  walkingPlayerIndex?: number,
  bumperRenderer?: BumperRenderer,
  defenseActiveOnly?: number,
  ceilingY?: number,
  frozenPlayerIndices?: number[],
  gravityMult?: number,
): void {
  // Tick respawn invulnerability frames for all alive players
  for (const p of players) {
    if (p.alive && p.respawnInvulnFrames > 0) {
      p.respawnInvulnFrames--;
    }
  }

  for (let pi = 0; pi < players.length; pi++) {
    const p = players[pi];
    if (!p.alive) continue;
    // Tutorial edgeguard freeze: skip physics for frozen defenders
    if (frozenPlayerIndices?.includes(pi)) continue;
    // Defense phase: only process the defending player
    if (defenseActiveOnly !== undefined && pi !== defenseActiveOnly) continue;
    const isGrappleRetracting = pi === grappleRetractingIndex;
    const isWalking = pi === walkingPlayerIndex;
    const ecb = lookupTank(p.tankId).ecbRadius ?? PHYSICS.ECB_RADIUS;

    // DI freeze — player with pending knockback stays at impact position.
    if (p.diPendingVx !== 0 || p.diPendingVy !== 0) continue;

    // Save pre-physics position for grapple terrain safety net
    const prevX = p.x;
    const prevY = p.y;

    // Settled latch — tank is at rest on ground, skip all physics until disturbed.
    if (p.settled) {
      const disturbed = p.vx !== 0 || p.vy !== 0 ||
        p.x !== p.settledX ||
        p.tumble || isGrappleRetracting || isWalking ||
        p.walkDeltaX !== 0 || p.walkCatchupTimer > 0;
      if (!disturbed) {
        p.walkDeltaX = 0;
        p.x = clampToWorldX(p.x, ecb);
        updateSquashSpring(p);
        continue;
      }
      p.settled = false;
      mirrorAirborne(p);
    }

    // Visual spin during ragdoll — dampened during recovery (slows toward upright)
    if (p.tumble) {
      let spinDelta = p.vx * TUMBLE.SPIN_RATE + p.vy * TUMBLE.SPIN_VY_RATE;
      if (p.tumbleRecoveryFrame > 0 && p.tumbleRecoveryMax > 0) {
        // Apply exponential damping: RECOVERY_SPIN_DAMP^framesInRecovery
        const damp = Math.pow(TUMBLE.RECOVERY_SPIN_DAMP, p.tumbleRecoveryFrame);
        spinDelta *= damp;
      }
      p.rotation += spinDelta;
    }

    // Build platform-aware terrain test for this player
    const platformTest = makePlatformTest(p);

    // ──── STATE A: Walking (probe-and-snap) ────
    // Probe-and-snap: collision handles uphill/walls, downward scan handles terrain following.
    // No vy manipulation — eliminates ground-stick oscillation on flat terrain.
    if ((isWalking || p.walkCatchupTimer > 0) && !p.tumble) {
      if (p.actionState.tag !== 'walking') mirrorWalking(p);
      p.burstFlying = false;    // walking = grounded
      p.liftActive = false;     // walking = grounded, no lift
      p.fastFallActive = false; // walking = grounded, no fast fall
      const walkDx = p.walkDeltaX;
      p.walkDeltaX = 0; // consumed

      const startX = p.x;
      const startY = p.y;

      // 1. Apply X movement intent
      if (walkDx !== 0) {
        p.x = clampToWorldX(p.x + walkDx, ecb);
      }

      // 2. Resolve terrain penetration — handles uphill (walkDx pushed into terrain) and walls
      const walkPen = resolveTerrainPenetration(p.x, p.y, ecb, platformTest);

      if (walkPen.contact) {
        // Uphill/embedded: push out of terrain
        p.x += walkPen.pushX;
        p.y += walkPen.pushY;

        // Wall check using smoothed normal (7×7 grid average — immune to 1-2 cell spikes)
        const { ny: surfNy } = getSurfaceNormal(p.x, p.y + ecb);
        if (Math.abs(surfNy) <= TRACTION.WALK_WALL_NY) {
          p.x = startX;
        }

        // Stabilize: if collision push overcorrected (feet floating above terrain),
        // nudge down to surface contact. Prevents bounce from push overestimation.
        const postFeetY = p.y + ecb;
        if (!platformTest(p.x, postFeetY)) {
          for (let dy = 1; dy <= 4; dy++) {
            if (platformTest(p.x, postFeetY + dy)) {
              p.y += dy - 1;
              break;
            }
          }
        }

        p.vy = 0;
        p.grounded = true;
      } else {
        // 3. No collision — probe downward for terrain following (gentle downhill)
        const feetY = p.y + ecb;
        let snapped = false;
        for (let dy = 0; dy <= TRACTION.GROUND_STICK; dy++) {
          if (platformTest(p.x, feetY + dy)) {
            if (dy > 0) p.y += dy - 1;
            p.vy = 0;
            p.grounded = true;
            snapped = true;
            break;
          }
        }

        if (!snapped) {
          // No ground within probe distance — airborne (ledge/cliff)
          const gMult = gravityMult ?? 1;
          const grav = (p.fastFallActive ? PHYSICS.GRAVITY * FAST_FALL.GRAVITY_MULT : PHYSICS.GRAVITY) * gMult;
          const maxFall = (p.fastFallActive ? FAST_FALL.MAX_FALL_SPEED : PHYSICS.MAX_FALL_SPEED) * gMult;
          p.vy += grav;
          p.vy = Math.min(p.vy, maxFall);
          p.y += p.vy;
          p.walkCatchupTimer = 0;
          p.squashVel += CLIMB_FX.DROP_IMPULSE;
          p.grounded = false;
        }
      }

      // Tick catchup timer
      if (!isWalking && p.walkCatchupTimer > 0) {
        p.walkCatchupTimer--;
        if (p.actionState.tag === 'walking') p.actionState.catchupTimer = p.walkCatchupTimer;
      }

      // --- Climb visual feedback ---
      const actualRise = startY - p.y;
      if (actualRise > CLIMB_FX.RISE_THRESHOLD) {
        if (p.climbDustCooldown <= 0) {
          if (particles) {
            particles.spawnClimbDust(p.x, p.y + ecb, Math.sign(walkDx || 1), p.color);
          }
          p.climbDustCooldown = CLIMB_FX.DUST_COOLDOWN;
        }
        if (!p.climbBurstFired) {
          if (screenShake) screenShake.trigger(CLIMB_FX.FIRST_BURST_SHAKE, 0.7);
          p.climbBurstFired = true;
        }
        p.squashVel -= CLIMB_FX.CLIMB_IMPULSE;
      } else {
        if (p.climbBurstFired) {
          p.squashVel += CLIMB_FX.ARRIVE_IMPULSE;
        }
        p.climbBurstFired = false;
      }
      if (p.climbDustCooldown > 0) p.climbDustCooldown--;

      // Walking done — settle directly instead of falling into physics-driven for a
      // transition frame (which would briefly flash 'airborne' before gravity re-settles).
      if (!isWalking && p.walkCatchupTimer <= 0 && p.vx === 0 && p.vy === 0) {
        p.settled = true;
        p.settledX = p.x;
        p.settledY = p.y;
        mirrorSettle(p);
      }
    } else {
      // ──── STATE B: Physics-driven ────
      // Unified path for gravity, ragdoll, knockback, grapple, landing — everything non-walking.
      // 1. Apply gravity (fast fall = doubled gravity + higher terminal velocity)
      const gMult = gravityMult ?? 1;
      const grav = (p.fastFallActive ? PHYSICS.GRAVITY * FAST_FALL.GRAVITY_MULT : PHYSICS.GRAVITY) * gMult;
      const maxFall = (p.fastFallActive ? FAST_FALL.MAX_FALL_SPEED : PHYSICS.MAX_FALL_SPEED) * gMult;
      p.vy += grav;
      p.vy = Math.min(p.vy, maxFall);

      // 2. Apply velocity — save pre-move feet Y for platform sweep
      const prevFeetY = p.y + ecb;
      p.x += p.vx;
      p.y += p.vy;

      // 2b. Platform sweep test — catch thin wireframe platforms that velocity would tunnel through.
      // Only when falling, not tumbling, not dropping through.
      if (p.vy > 0 && !p.tumble && p.platformDropTimer <= 0) {
        const curFeetY = p.y + ecb;
        const startGy = Math.floor(prevFeetY / TERRAIN.CELL_SIZE);
        const endGy = Math.floor(curFeetY / TERRAIN.CELL_SIZE);
        if (endGy > startGy) {
          const gx = Math.floor(p.x / TERRAIN.CELL_SIZE);
          for (let gy = startGy; gy <= endGy; gy++) {
            const cell = getCellAt(gx * TERRAIN.CELL_SIZE + TERRAIN.CELL_SIZE * 0.5, gy * TERRAIN.CELL_SIZE + TERRAIN.CELL_SIZE * 0.5);
            if (cell === CELL.WIREFRAME) {
              // Snap feet to top of this wireframe cell
              p.y = gy * TERRAIN.CELL_SIZE - ecb;
              p.vy = 0;
              p.grounded = true;
              break;
            }
          }
        }
      }

      // 3. Apply friction (skip during lift — lift has its own speed cap)
      if (!p.liftActive) {
        p.vx *= getFrameFriction(p, p.grounded, ecb);
        if (Math.abs(p.vx) < PHYSICS.HORIZONTAL_STOP_THRESHOLD) {
          p.vx = 0;
        }
      }

      // 4. Resolve terrain penetration
      const pen = resolveTerrainPenetration(p.x, p.y, ecb, platformTest);

      if (pen.contact) {
        // During ragdoll immunity, only correct on near-flat surfaces — fly through slopes/rims
        if (p.tumble && p.launchArmor > 0) {
          if (Math.abs(pen.ny) > TUMBLE.IMMUNITY_SNAP_NY) {
            // Near-flat ground — correct position, dampen vy
            p.x += pen.pushX;
            p.y += pen.pushY;
            if (p.vy > 0) p.vy *= TUMBLE.IMMUNITY_VY_DAMP;
          }
          // Else: steep surface — fly through (immunity escape)
        } else {
          // Push out of terrain
          p.x += pen.pushX;
          p.y += pen.pushY;

          if (p.tumble) {
            // Ragdoll contact response — classify by surface normal
            const absNy = Math.abs(pen.ny);
            const impactSpeed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);

            if (absNy > SLOPE.WALL_NY) {
              // Slope or flat ground — tangent project (ride the slope)
              const tx = -pen.ny;
              const ty = pen.nx;
              const slideDot = p.vx * tx + p.vy * ty;

              if (impactSpeed < TUMBLE.TECH_THRESHOLD) {
                // Slow — just project along tangent
                p.vx = slideDot * tx;
                p.vy = slideDot * ty;
              } else {
                // Fast — tangent project with friction
                p.vx = slideDot * tx * TUMBLE.SLOPE_FRICTION;
                p.vy = slideDot * ty * TUMBLE.SLOPE_FRICTION;

                const intensity = Math.min(1.0, impactSpeed / TUMBLE.BOUNCE_INTENSITY_DIVISOR) * TUMBLE.SLOPE_IMPACT_SCALE;
                if (particles) {
                  particles.spawnBounceImpact(p.x, p.y + ecb, p.color, intensity);
                }
              }
            } else {
              // Wall/steep — reflection bounce
              reflectBounce(p, pen.nx, pen.ny);

              const intensity = Math.min(1.0, impactSpeed / TUMBLE.BOUNCE_INTENSITY_DIVISOR);
              if (particles) {
                particles.spawnBounceImpact(p.x, p.y + ecb, p.color, intensity);
              }
              if (screenShake) {
                screenShake.trigger(TUMBLE.BOUNCE_SHAKE * intensity, TUMBLE.BOUNCE_SHAKE_DECAY);
              }
            }

            p.grounded = true;
          } else if (isGrappleRetracting) {
            // Grapple retraction: bounce off terrain, pull force keeps driving
            const impactSpeed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
            if (impactSpeed < TUMBLE.TECH_THRESHOLD) {
              // Gentle contact — pop upward to skip terrain bumps
              p.vy = -GRAPPLE.RETRACT_GROUND_POP;
            } else {
              // Bounce
              reflectBounce(p, pen.nx, pen.ny);
            }
            p.grounded = true;
          } else {
            // Non-ragdoll, non-grapple landing
            p.vy = 0;
            p.squashVel -= CLIMB_FX.LAND_IMPULSE;
            exitTumble(p);
            p.burstFlying = false;
            p.liftActive = false;
            p.fastFallActive = false;
            p.grounded = true;

            // Settle check — if stopped, latch
            if (p.vx === 0) {
              p.settled = true;
              p.settledX = p.x;
              p.settledY = p.y;
              mirrorSettle(p);
            }
          }
        }
      } else {
        // No contact — airborne
        p.grounded = false;
        if (p.actionState.tag !== 'airborne' && !p.actionState.tag.startsWith('tumble') && p.actionState.tag !== 'di_frozen') {
          mirrorAirborne(p);
        }
      }

      // Clear unconsumed walk intent
      p.walkDeltaX = 0;
    }

    // ──── Post-physics: shared systems ────

    // Platform drop timer countdown
    if (p.platformDropTimer > 0) p.platformDropTimer--;

    // Detect onWireframe — scan grid cells below feet for wireframe.
    // Sand may drape over wireframe (thick layer), so scan in grid-cell steps up to 16 cells deep.
    if (p.grounded) {
      const feetY = p.y + ecb;
      const startGy = Math.floor(feetY / TERRAIN.CELL_SIZE) + 1;
      const gx = Math.floor(p.x / TERRAIN.CELL_SIZE);
      p.onWireframe = false;
      for (let gy = startGy; gy < startGy + 16; gy++) {
        const idx = gy * TERRAIN.GRID_WIDTH + gx;
        const grid = getGrid();
        if (!grid || gy >= TERRAIN.GRID_HEIGHT) break;
        const cell = grid[idx];
        if (cell === CELL.WIREFRAME) {
          p.onWireframe = true;
          break;
        }
        if (cell === CELL.EMPTY) break; // gap below = no wireframe support
      }
    } else {
      p.onWireframe = false;
    }

    // Terrain embedding safety — grapple pull can push through thin walls during ragdoll immunity
    if (isGrappleRetracting && isPointInTerrain(p.x, p.y)) {
      p.x = prevX;
      p.y = prevY;
      p.vx *= -GRAPPLE.EMBED_BOUNCE_RETENTION;
      p.vy *= -TUMBLE.UNTECH_BOUNCE_RETENTION;
    }

    // Ragdoll timer countdown — skip during grapple retraction (pull force drives ragdoll)
    if (p.tumble && !isGrappleRetracting) {
      if (p.hitstunFrames > 0) {
        // ── Hitstun phase ──
        p.hitstunFrames--;
        if (p.actionState.tag === 'tumble_hitstun') p.actionState.hitstun = p.hitstunFrames;
        if (p.hitstunFrames <= 0) {
          // Hitstun expired — check variant
          const wasControlledLaunch = p.burstFlying || p.grappleLaunched;
          if (wasControlledLaunch) {
            // Burst/grapple variant — immediate exit with control snap (no recovery)
            exitTumble(p);
            p.controlRestored = true;
            p.squash = BURST.CONTROL_SNAP_SQUASH;
            p.squashVel = BURST.CONTROL_SNAP_IMPULSE;
            if (particles) particles.spawnControlSnap(p.x, p.y, p.color);
            if (screenShake) screenShake.trigger(BURST.CONTROL_SHAKE, 0.85);
          } else if (p.tumbleRecoveryMax > 0) {
            // Knockback variant — transition to recovery sub-state
            p.tumbleRecoveryFrame = 0;
            mirrorEnterRecovery(p, p.actionState.tag === 'tumble_hitstun' ? p.actionState.variant : 'knockback');
          } else {
            // No recovery frames (shouldn't happen, safety)
            exitTumble(p);
          }
        } else if (p.launchArmor <= 0) {
          // Speed settle during hitstun — skip for burst ragdoll
          if (p.grounded && !p.burstFlying) {
            const speed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
            if (speed < TUMBLE.CANCEL_SPEED) {
              exitTumble(p);
            }
          }
        }
      } else if (p.tumbleRecoveryFrame < p.tumbleRecoveryMax) {
        // ── Recovery phase ──
        p.tumbleRecoveryFrame++;
        if (p.actionState.tag === 'tumble_recovery') p.actionState.recoveryFrame = p.tumbleRecoveryFrame;
        if (p.tumbleRecoveryFrame >= p.tumbleRecoveryMax) {
          exitTumble(p);
        } else if (p.grounded) {
          // Speed settle during recovery
          const speed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
          if (speed < TUMBLE.CANCEL_SPEED) {
            exitTumble(p);
          }
        }
      }
    }

    // Burst flight trail (gold) — takes priority over ragdoll trail
    if (p.burstFlying && particles) {
      const burstSpeed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
      if (burstSpeed >= BURST_TRAIL.MIN_SPEED) {
        p.burstTrailCooldown--;
        if (p.burstTrailCooldown <= 0) {
          particles.spawnBurstTrail(p.x, p.y, p.vx, p.vy, burstSpeed);
          p.burstTrailCooldown = burstSpeed >= BURST_TRAIL.TIER_HIGH ? BURST_TRAIL.COOLDOWN_HIGH
            : burstSpeed >= BURST_TRAIL.TIER_MED ? BURST_TRAIL.COOLDOWN_MED
            : BURST_TRAIL.COOLDOWN_LOW;
        }
      } else {
        p.burstFlying = false;
      }
    }
    // Fast fall trail (cyan) — downward streaks during active fast fall
    if (p.fastFallActive && !p.tumble && particles) {
      p.fastFallTrailCooldown--;
      if (p.fastFallTrailCooldown <= 0) {
        particles.spawnFastFallTrail(p.x, p.y, p.vy, p.color);
        p.fastFallTrailCooldown = FAST_FALL.TRAIL_COOLDOWN;
      }
    }
    // Lift trail (player color) — soft wisps during active lift
    if (p.liftActive && !p.tumble && particles) {
      p.liftTrailCooldown--;
      if (p.liftTrailCooldown <= 0) {
        particles.spawnLiftTrail(p.x, p.y, p.vx, p.vy, p.color);
        const liftSpeed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
        p.liftTrailCooldown = liftSpeed >= HOP.LIFT_TRAIL_TIER_FAST ? HOP.LIFT_TRAIL_COOLDOWN_FAST
          : liftSpeed >= HOP.LIFT_TRAIL_TIER_CRUISE ? HOP.LIFT_TRAIL_COOLDOWN_CRUISE
          : HOP.LIFT_TRAIL_COOLDOWN_HOVER;
      }
    }
    // Ragdoll trail (player color) — only for non-burst ragdolls (knockback)
    else if (p.tumble && particles) {
      const trailSpeed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
      if (trailSpeed >= LAUNCH_TRAIL.MIN_SPEED) {
        p.tumbleTrailCooldown--;
        if (p.tumbleTrailCooldown <= 0) {
          particles.spawnLaunchTrail(p.x, p.y, p.vx, p.vy, p.color, trailSpeed);
          p.tumbleTrailCooldown = trailSpeed >= LAUNCH_TRAIL.TIER_KO ? LAUNCH_TRAIL.COOLDOWN_KO
            : trailSpeed >= LAUNCH_TRAIL.TIER_HIGH ? LAUNCH_TRAIL.COOLDOWN_HIGH
            : trailSpeed >= LAUNCH_TRAIL.TIER_MED ? LAUNCH_TRAIL.COOLDOWN_MED
            : LAUNCH_TRAIL.COOLDOWN_LOW;
        }
      }
    }

    // Wall bounce / open sides — controlled by SIDE_DEATH.OPEN_SIDES
    const wallBounceResult = handleWorldWallBounce(p, ecb);
    if (wallBounceResult) {
      if (particles) {
        const contactX = wallBounceResult.side === 'left'
          ? WORLD.WORLD_LEFT : WORLD.WORLD_RIGHT;
        particles.spawnWallBounce(
          contactX, p.y,
          wallBounceResult.side === 'left' ? 1 : -1,
          p.color,
          wallBounceResult.intensity,
          wallBounceResult.isSpike,
        );
      }
      if (screenShake) {
        const shake = wallBounceResult.isSpike
          ? WALL_BOUNCE.SPIKE_SHAKE_INTENSITY
          : WALL_BOUNCE.SHAKE_INTENSITY;
        const decay = wallBounceResult.isSpike
          ? WALL_BOUNCE.SPIKE_SHAKE_DECAY
          : WALL_BOUNCE.SHAKE_DECAY;
        screenShake.trigger(shake * wallBounceResult.intensity, decay);
      }
      if (bumperRenderer) {
        bumperRenderer.triggerFlash(wallBounceResult);
      }
    } else if (!p.tumble || !SIDE_DEATH.OPEN_SIDES) {
      // Non-tumble tanks always clamp. Tumble tanks clamp when walls are on.
      p.x = clampToWorldX(p.x, ecb);
    }

    // Ceiling bounce — damage-scaled bounce off ceiling (replaces sky death)
    if (ceilingY !== undefined) {
      const ceilResult = handleCeilingBounce(p, ceilingY, ecb);
      if (ceilResult) {
        if (particles) {
          particles.spawnCeilingBounce(p.x, ceilingY, p.color, ceilResult.intensity);
        }
        if (screenShake) {
          screenShake.trigger(
            CEILING_BOUNCE.SHAKE_INTENSITY * ceilResult.intensity,
            CEILING_BOUNCE.SHAKE_DECAY,
          );
        }
      }
    }

    // Ragdoll immunity countdown
    if (p.launchArmor > 0) {
      p.launchArmor--;
      if (p.actionState.tag === 'tumble_hitstun') p.actionState.armor = p.launchArmor;
    }

    // Squash-stretch spring integration
    updateSquashSpring(p);

    // DEV: verify actionState mirrors boolean flags
    assertActionStateConsistent(p, `P${pi}`);
  }
}
